import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@comps/ui/card";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@comps/ui/chart";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ChartDataItem, getWidgetStyle, PieChartData, WidgetProps } from "@dto/Dashboard";
import { AggregationType } from "@dto/fields";
import { PathsResponse } from "@dto/records";
import { ChartColorType, getColor } from "@utils/colors";
import { getPaths } from "@utils/data";
import { pathKeyToPath } from "@utils/model";
import { toastError } from "@utils/toasts";
import { valueToString } from "@utils/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pie, PieChart } from "recharts";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";


export function PieChartCard(props: WidgetProps) {
    const widget = props.widget.data as PieChartData;

    const objectInfos = useModelStore(state => state.objectInfos);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const [chartData, setChartData] = useState<ChartDataItem[]>([]);
    const [chartConfig, setChartConfig] = useState<ChartConfig>({});
    const { t } = useTranslation();
    const valuePathKey = widget.valuePath;
    const valueAggregation = widget.valueAggregation;
    const categoryPathKey = widget.categoryPath;
    const categoryAggregation = widget.categoryAggregation;
    const categorySortType = widget.categorySortType;
    const filter = widget.filter;

    const valuePath = pathKeyToPath(valuePathKey, relationByKeys);
    const categoryPath = pathKeyToPath(categoryPathKey, relationByKeys);

    useEffect(() => {
        if (!workspace) {
            return;
        }
        const srcObjectKey = valuePath?.srcObject ?? valuePath?.object;
        if (!valuePath || !categoryPath || !srcObjectKey) {
            return;
        }

        const sortings = [{ pathKey: categoryPathKey!, sort: categorySortType }];
        getPaths(workspace._key, srcObjectKey, [valuePath, categoryPath], null, filter, sortings, null, null, null, aggregationMap).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                } else {
                    const pathsResponse = response as PathsResponse;
                    if (pathsResponse.lines && pathsResponse.lines.length > 0) {
                        const newValues = new Map<string, number>()
                        const categoryField = categoryPath?.field ? fieldByKeys?.get(categoryPath.field) : undefined;
                        for (const line of pathsResponse.lines) {
                            let category = valueToString(line.items[1].value, categoryField!, categoryAggregation ?? undefined, undefined, t, "", fieldByKeys);
                            if (!category) {
                                category = t("dashboard.label.empty");
                            }
                            newValues.set(category, line.items[0].value ?? 0)
                        }
                        const chartData: ChartDataItem[] = []
                        const chartConfig: ChartConfig = {
                            value: {
                                label: (valueField?.name ?? ""),
                            },
                        }

                        let index = 0;
                        newValues.forEach((value, key) => {
                            const chartDataItem: ChartDataItem = {
                                splitBy: key,
                                value: value,
                                fill: getColor(ChartColorType, index),
                                category: null
                            }
                            chartData.push(chartDataItem)
                            chartConfig[key as string] = {
                                label: key,
                                color: getColor(ChartColorType, index),
                            }
                            index++;
                        })

                        setChartData(chartData);
                        setChartConfig(chartConfig);
                    } else {
                        // setValues(new Map());
                        setChartData([]);
                        setChartConfig({});
                    }
                }
            }
        })
    }, [objectInfos, fieldByKeys, widget, filter]);


    if (!valuePathKey || !categoryPathKey) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    if (!workspace) {
        return <div>{t("dashboard.overview.not.configured")}</div>;
    }

    const valueField = fieldByKeys?.get(valuePath?.field ?? "");

    const aggregationMap = new Map<string, AggregationType>();
    if (valuePathKey && valueAggregation) {
        aggregationMap.set(valuePathKey, valueAggregation);
    }
    if (categoryPathKey && categoryAggregation) {
        aggregationMap.set(categoryPathKey, categoryAggregation);
    }

    if (chartData.length === 0) {
        return <div>{t("dashboard.overview.no.data")}</div>;
    }

    const valueFormat = valueField?.format;
    const formatter = (value: any) => {
        if (value) {
            return "valeur : " + valueToString(value, valueField!, valueAggregation ?? undefined, valueFormat, t, "", fieldByKeys);
        }
        return "";
    }

    // Calculer le total pour les pourcentages
    const total = chartData.reduce((sum, item) => sum + (item.value || 0), 0);

    const labelValue = (props.widget.showValues === true || props.widget.showPercentage === true) ?
        (labelProps: any) => {
            if (labelProps.value) {
                if (props.widget.showValues) {
                    let label = valueToString(labelProps.value, valueField!, valueAggregation ?? undefined, valueFormat, t, "", fieldByKeys);
                    if (props.widget.showPercentage && total > 0) {
                        const percentage = ((labelProps.value / total) * 100).toFixed(1);
                        label = `${label} (${percentage}%)`;
                    }
                    return label;
                } else if (props.widget.showPercentage && total > 0) {
                    const percentage = ((labelProps.value / total) * 100).toFixed(1);
                    return `${percentage}%`;
                }
            }
            return "";
        } : false;

    const style = getWidgetStyle(props.widget);
    return (
        <Card>
            {props.widget.title &&
                <CardHeader className="items-center pb-0">
                    <CardTitle>{props.widget.title}</CardTitle>
                    {props.widget.description &&
                        <CardDescription>
                            {props.widget.description}
                        </CardDescription>
                    }
                </CardHeader>
            }
            <CardContent className="pb-0">
                <ChartContainer
                    config={chartConfig}
                    className="mx-auto aspect-square" style={style}>
                    <PieChart>
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel separator=" - "
                                formatter={formatter} />}
                        />
                        <Pie data={chartData} dataKey="value" nameKey="splitBy" label={labelValue} />
                        {props.widget.showLegend && <ChartLegend content={<ChartLegendContent className="flex-wrap" />} />}
                    </PieChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}